<script setup lang="ts">
import SportcardStatus from '@/components/Sportcard/SportcardStatus.vue';
import { getDateInHoursAndMinutes } from '@/helpers/date.helper';
import { getAgeRangeLabel } from '@/helpers/activity.helper';
import { formatCurrency } from '@/helpers/currency.helper';
import { Activiteit } from '@/repositories/activiteit.repository';
import { usePlatformStore } from '@/stores/platform.store';
import { computed } from 'vue';

const props = defineProps<{
    activity: Activiteit,
    activitySingular: string,
    activityPlural: string,
}>();

const emits = defineEmits<{
    (e: 'selected', activity: Activiteit): void
}>();

const platformStore = usePlatformStore();

const availableSpots = computed(() => {
    if (props.activity.AantalPlaatsen <= props.activity.AantalInschrijvingen) {
        return 0;
    }

    if (props.activity.TotaalAantalPlaatsen != props.activity.AantalPlaatsen) {
        const availablePlaces = props.activity.TotaalAantalPlaatsen - props.activity.AantalPlaatsen;
        const availablePlacesPlatform = props.activity.AantalPlaatsen - props.activity.AantalInschrijvingen;

        if ((availablePlaces > props.activity.AantalPlaatsen) || (availablePlaces > availablePlacesPlatform)) {
            return availablePlacesPlatform;
        }

        return availablePlaces
    }

    return props.activity.TotaalAantalPlaatsen - props.activity.TotaalAantalInschrijvingen;
});

const registrationStartDate = computed(() => {
    if (!props.activity.InschrijvenVanafDatum) {
        return undefined;
    }
    return new Date(props.activity.InschrijvenVanafDatum);
});

const registrationEndDate = computed(() => {
    if (!props.activity.InschrijvenTotDatum) {
        return undefined;
    }
    return new Date(props.activity.InschrijvenTotDatum);
});

const isRegistrationStarted = computed(() => {
    if (!registrationStartDate.value) {
        return true;
    }

    const now = new Date();
    const from = registrationStartDate.value;
    return from <= now;
});

const isRegistrationEnded = computed(() => {
    if (!registrationEndDate.value) {
        return false;
    }

    const now = new Date();
    const to = registrationEndDate.value;
    return now > to;
});

const registrationStatusClass = computed(() => {
    const prefix = " upcoming-activities-card__status--";
    const availablePlaces = availableSpots.value;

    if (props.activity.Boekingstype !== 1) {
        return undefined;
    }

    if (availablePlaces <= 0) {
        return prefix + "danger";
    }
    if (availablePlaces < 3) {
        return prefix + "warning";
    }
    if (availablePlaces > 10) {
        return prefix + "success";
    }

    return undefined;
});

const availableSpotsText = computed(() => {
    if (availableSpots.value == 1) {
        return availableSpots.value + ' plek vrij'
    } else if (availableSpots.value <= 15) {
        return availableSpots.value + ' plekken vrij'
    } else {
        return 'Voldoende plekken'
    }
});

const distanceInKm = computed(() => {
    const kilometers = props.activity.LocatieItem.Afstand / 1000;
    return +(Math.round(Number(kilometers + "e+2")) + "e-2"); // Round two decimals
});

const bookingUrl = computed(() => `/activiteit-detail/reserveren?id=${props.activity.SportmomentId}`);
const activityUrl = computed(() => `/activiteit-detail?id=${props.activity.SportmomentId}`);

const startRedirect = () => {
    emits('selected', props.activity);
}

const ageRangeLabel = computed(() => getAgeRangeLabel(props.activity.MinLeeftijd, props.activity.MaxLeeftijd));

const isSamePlatform = computed(() => {
    return props.activity.PlatformItem.Id === platformStore.platformId
});

const isAbonnement = computed(() => props.activity.BetalingsType === 'Abonnement');
const abonnementPrijs = computed(() => `${formatCurrency(props.activity.Kosten ?? 0)} per maand`);

</script>

<template>
    <div class="upcoming-activities-card">

        <div class="upcoming-activities-card__aside-top">
            <div class="upcoming-activities-card__image">
                <picture v-if="!activity.AfbeeldingUrl">
                    <img class="img-fluid" :src="activity.SportaanbiederItem.Logo + '?width=164&height=164&mode=crop'"
                        alt="" />
                </picture>

                <a v-else :href="activityUrl" target="_self">
                    <picture>
                        <img class="img-fluid" :src="activity.AfbeeldingUrl + '?width=640&height=360&mode=crop'" alt="" />
                    </picture>
                </a>
                <span v-if="ageRangeLabel" class="upcoming-activities-card__age-badge">{{ ageRangeLabel }}</span>
            </div>

            <div class="upcoming-activities-card__head">
                <div class="upcoming-activities-card__provider">
                    <a :href="activityUrl" target="_self" class="upcoming-activities-card__title">
                        <h3 class="h5">{{ activity.Naam }}</h3>
                    </a>
                    <div class="upcoming-activities-card__provider-name">
                        <span>{{ activity.SportaanbiederItem.Naam }}</span>
                    </div>
                </div>
                <div v-if="activity.SportaanbiederItem.ReviewCount" class="upcoming-activities-card__provider-rating">
                    <span class="icon icon-star-active" aria-hidden="true" />
                    <span class="upcoming-activities-card__provider-rating-score">
                        {{
                            Math.round(activity.SportaanbiederItem.ReviewScore * 10) / 10
                        }}
                    </span>
                    <span class="upcoming-activities-card__provider-rating-count">
                        ({{
                            activity.SportaanbiederItem.ReviewCount
                        }})
                    </span>
                </div>
            </div>

            <div class="upcoming-activities-card__lessons" v-if="activity.Lessen.length">
                <span class="font-weight-bold">{{ activity.Lessen.length }} Lessen</span>
            </div>
            <div class="upcoming-activities-card__info">
                <div class="upcoming-activities-card__date">
                    <span class="icon icon-calendar" aria-hidden="true" />
                    <span>
                        {{
                            new
                                Date(activity.BeginDatum).toLocaleDateString('nl-NL', {
                                    weekday: 'long',
                                    month: 'long',
                                    day: 'numeric'
                                })
                        }}
                    </span>
                </div>
                <div class="upcoming-activities-card__time">
                    <span class="icon icon-clock" aria-hidden="true" />
                    <span>
                        {{ getDateInHoursAndMinutes(activity.BeginDatum) }} - {{
                            getDateInHoursAndMinutes(activity.EindDatum) }}
                    </span>
                </div>

                <div class="upcoming-activities-card__location">
                    <div class="upcoming-activities-card__location-icon">
                        <span class="icon icon-map" aria-hidden="true"></span>
                    </div>
                    <div class="upcoming-activities-card__location-info">
                        <div v-if="!platformStore.platform?.IsSportSpecific"
                            class="upcoming-activities-card__location-address">
                            {{ activity.LocatieItem.Naam }}
                            <span
                                v-if="platformStore.platform?.IsLocal ? activity.LocatieItem.Adres : activity.LocatieItem.Woonplaats">
                                ,&nbsp;
                                {{
                                    platformStore.platform?.IsLocal ? activity.LocatieItem.Adres :
                                        activity.LocatieItem.Woonplaats
                                }}
                            </span>
                        </div>
                        <div v-if="activity.LocatieItem.Afstand" class="upcoming-activities-card__location-distance">
                            (± {{ distanceInKm }}km)
                        </div>
                    </div>
                </div>
            </div>


            <div class="sport-card-refined__free" v-if="platformStore.platformId == 53">
                <div class="sport-card-refined__free-icon">
                    <span class="icon icon-info" aria-hidden="true"></span>
                </div>
                <div class="sport-card-refined__free-info">
                    <div class="sport-card-refined__free-address">
                        Ook zonder aanmelding ben je welkom
                    </div>
                </div>
            </div>
        </div>

        <div class="upcoming-activities-card__aside-bottom">
            <div class="upcoming-activities-card__aside-bottom-info">
                <SportcardStatus :is-registration-ended="isRegistrationEnded"
                    :is-registration-started="isRegistrationStarted" :registration-start-date="registrationStartDate"
                    :registration-status-class="registrationStatusClass" :available-spots="availableSpots"
                    :activity-singular="activitySingular" :available-spots-text="availableSpotsText"
                    :entity="activity" />

                <div class="upcoming-activities-card__price">
                    <p class="mb-0 h5">
                        <span v-if="activity.BetalingsType === 'Credits'">1 credit</span>
                        <span v-else-if="activity.BetalingsType === 'Gratis'">Gratis</span>
                        <span v-else-if="activity.BetalingsType === 'Abonnement'">{{ abonnementPrijs }}</span>
                        <span v-else-if="activity.BetalingsType === 'Op locatie'" class="sport-card-refined__price-costs">
                            <template v-if="(activity.Kosten ?? 0) > 0">
                                <span class="sport-card-refined__price-currency">€</span>
                                <span>{{ (activity.Kosten ?? 0).toFixed(2) }}</span>
                            </template>
                            <span class="font-size-sm">op locatie</span>
                        </span>
                        <span v-else class="upcoming-activities-card__price-costs">
                            <span class="upcoming-activities-card__price-currency">€</span>
                            <span>{{ (activity.Kosten ?? 0).toFixed(2) }}</span>
                        </span>
                    </p>
                </div>
            </div>

            <div class="upcoming-activities-card__aside-bottom-buttons">
                <a class="btn btn-sm btn-sport-in-light" :href="activityUrl" target="_self"
                    :aria-label="'Meer informatie over ' + activity.Naam">
                    Meer info
                </a>
                <button v-if="activity.Boekingstype === 1 && availableSpots >= 1 && !isSamePlatform && !isAbonnement"
                    @click="startRedirect"
                    class="d-flex align-items-center justify-content-center btn btn-sm btn-outline-primary"
                    :class="(!isRegistrationStarted || isRegistrationEnded) && 'disabled'"
                    :disabled="!isRegistrationStarted || isRegistrationEnded" :aria-label="activity.Naam + ' boeken'">
                    Boeken&nbsp;
                    <span class="icon icon-external-link" aria-hidden="true" />
                </button>

                <a v-else-if="activity.Boekingstype === 1 && availableSpots >= 1 && !isAbonnement" :href="bookingUrl" target="_self"
                    class="d-flex align-items-center justify-content-center btn btn-sm btn-outline-primary"
                    :class="(!isRegistrationStarted || isRegistrationEnded) && 'disabled'"
                    :disabled="!isRegistrationStarted || isRegistrationEnded" :aria-label="activity.Naam + ' boeken'">
                    Boeken
                </a>

                <a v-else-if="activity.Boekingstype === 2" :href="activity.ExterneBoekingsUrl" target="_blank"
                    class="d-flex align-items-center justify-content-center btn btn-sm btn-outline-primary"
                    :class="(!isRegistrationStarted || isRegistrationEnded) && 'disabled'"
                    :disabled="!isRegistrationStarted || isRegistrationEnded" :aria-label="activity.Naam + ' boeken'">
                    Extern boeken&nbsp;
                    <span class="icon icon-external-link" aria-hidden="true" />
                </a>

                <a v-if="activity.Boekingstype === 1 && isRegistrationStarted && !isRegistrationEnded && availableSpots < 1"
                    :href="activityUrl" target="_self"
                    class="btn btn-sm btn-outline-primary upcoming-activities-card__waitlist"
                    :aria-label="'Meld je aan voor de wachtrij voor ' + activity.Naam">
                    Wachtrij
                </a>
                <Tooltip v-if="activity.Boekingstype === 1 && availableSpots < 1" :triggers="['click', 'hover']">
                    <span class="icon-info1 ml-1" aria-hidden="true" />

                    <template #popper>
                        <div style="background: none; font-size: 0.875rem">
                            Als je jezelf op de wachtrij zet voor deze {{ activitySingular }} en er een plek
                            vrijkomt, ontvang
                            je hierover een e-mail.
                        </div>
                    </template>
                </Tooltip>
            </div>
        </div>
    </div>
</template>